import { useEffect, useRef, useState, useCallback, memo, type ReactNode } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import { TextLayer } from 'pdfjs-dist'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/outline'
import { rotateDocumentPdfPage } from '@/services/documents'
import {
  normalizeBbox,
  pymupdfBboxToPdfRect,
  rectsOverlap,
  spanViewportRect,
  viewportRectFromConverted,
  type BboxInput,
} from './pdfViewerHighlight'

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

interface HighlightData {
  amount: string | null
  description: string | null
  itemCode?: string | null
}

interface PdfViewerProps {
  documentId: number
  highlightData: HighlightData | null
  downloadFilename?: string
  pageNumber?: number | null
  bbox?: BboxInput
  toolbarBeforeDownload?: ReactNode
}

const ZOOM_LEVELS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0] as const
const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.indexOf(1.5)

function generateAmountPattern(amount: string): RegExp | null {
  const num = parseFloat(amount)
  if (isNaN(num) || num === 0) return null

  const variants: string[] = []
  const absNum = Math.abs(num)

  variants.push(absNum.toFixed(2))
  if (absNum === Math.floor(absNum)) {
    variants.push(String(Math.floor(absNum)))
  }
  variants.push(absNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
  if (absNum >= 1000) {
    variants.push(absNum.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }))
  }

  const withCurrency = variants.flatMap(v => [v, `\\$${v}`, `\\$ ${v}`])
  const allVariants = [...new Set(withCurrency)]

  const escapedVariants = allVariants.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const inner = escapedVariants.join('|')
  return new RegExp(`(?<![\\d,])(${inner})(?![\\d,.])`, 'gi')
}


function PdfViewer({ documentId, highlightData, downloadFilename, pageNumber, bbox, toolbarBeforeDownload }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const pageWrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)
  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null)
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null)
  const textLayerInstanceRef = useRef<TextLayer | null>(null)
  const preservedPageRef = useRef<number | null>(null)
  const isInitialDocumentLoadRef = useRef(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX)
  const [downloading, setDownloading] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [rotateError, setRotateError] = useState<string | null>(null)
  const [pdfCacheKey, setPdfCacheKey] = useState(() => Date.now())
  const zoomIndexRef = useRef(zoomIndex)
  zoomIndexRef.current = zoomIndex
  const bboxViewportRectRef = useRef<{
    left: number
    top: number
    width: number
    height: number
  } | null>(null)
  const highlightDataRef = useRef<HighlightData | null>(highlightData)
  highlightDataRef.current = highlightData

  const updateHighlights = useCallback((data: HighlightData | null) => {
    if (!textLayerRef.current) return

    const bboxRect = bboxViewportRectRef.current
    const amountPattern = data?.amount ? generateAmountPattern(data.amount) : null
    const itemCode = data?.itemCode?.trim() ?? ''
    const spans = textLayerRef.current.querySelectorAll('span')

    spans.forEach((span) => {
      const spanText = span.textContent || ''
      let matches = false

      if (bboxRect) {
        matches = rectsOverlap(spanViewportRect(span as HTMLElement), bboxRect)
      } else {
        if (amountPattern) {
          try {
            if (amountPattern.test(spanText)) {
              matches = true
            }
            amountPattern.lastIndex = 0
          } catch {
            // Ignore regex errors
          }
        }

        if (!matches && itemCode.length >= 2) {
          if (spanText.toLowerCase().includes(itemCode.toLowerCase())) {
            matches = true
          }
        }
      }

      if (matches) {
        span.classList.add('amount-hover-highlight')
      } else {
        span.classList.remove('amount-hover-highlight')
      }
    })
  }, [])

  const renderPage = useCallback(async (pageNum: number, zoom: number) => {
    if (!pdfDocRef.current || !canvasRef.current || !textLayerRef.current) return

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }

    if (textLayerInstanceRef.current) {
      textLayerInstanceRef.current.cancel()
      textLayerInstanceRef.current = null
    }

    const page = await pdfDocRef.current.getPage(pageNum)
    const canvas = canvasRef.current
    const textLayerDiv = textLayerRef.current

    const viewport = page.getViewport({ scale: zoom })

    const wrapEl = pageWrapRef.current
    if (wrapEl) {
      wrapEl.style.setProperty('--scale-factor', String(viewport.scale))
      wrapEl.style.setProperty('--user-unit', String(page.userUnit))
    }

    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const context = canvas.getContext('2d')
    if (!context) return

    const renderTask = page.render({
      canvasContext: context,
      viewport,
      canvas,
    })
    renderTaskRef.current = renderTask

    try {
      await renderTask.promise
    } catch (err) {
      if ((err as Error).name === 'RenderingCancelledException') {
        return
      }
      throw err
    }

    const pageHeight = page.view[3]! - page.view[1]!
    const normalizedBbox = normalizeBbox(bbox)
    if (normalizedBbox) {
      const pdfRect = pymupdfBboxToPdfRect(normalizedBbox, pageHeight)
      const converted = viewport.convertToViewportRectangle(pdfRect)
      const { left, top, width, height } = viewportRectFromConverted(converted)
      bboxViewportRectRef.current = { left, top, width, height }
      context.save()
      context.strokeStyle = 'rgba(250, 204, 21, 0.95)'
      context.lineWidth = 2
      context.fillStyle = 'rgba(250, 204, 21, 0.25)'
      context.fillRect(left, top, width, height)
      context.strokeRect(left, top, width, height)
      context.restore()
    } else {
      bboxViewportRectRef.current = null
    }

    textLayerDiv.innerHTML = ''

    const textContent = await page.getTextContent()

    const textLayer = new TextLayer({
      textContentSource: textContent,
      viewport: viewport,
      container: textLayerDiv,
    })

    textLayerInstanceRef.current = textLayer
    await textLayer.render()
    updateHighlights(highlightDataRef.current)
  }, [bbox, updateHighlights])

  useEffect(() => {
    isInitialDocumentLoadRef.current = true
    setRotateError(null)
    setZoomIndex(DEFAULT_ZOOM_INDEX)
  }, [documentId])

  useEffect(() => {
    let cancelled = false

    async function loadPdf() {
      setLoading(true)
      setError(null)

      try {
        const pdfUrl = `/api/documents/${documentId}/pdf-content?_=${pdfCacheKey}`
        // ERRORS only: suppress noisy worker warnings (e.g. "TT: undefined function: 32") from
        // embedded fonts in many real-world PDFs; rendering is unaffected.
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          verbosity: pdfjsLib.VerbosityLevel.ERRORS,
        })
        loadingTaskRef.current = loadingTask
        const pdf = await loadingTask.promise

        if (cancelled) return

        pdfDocRef.current = pdf
        setTotalPages(pdf.numPages)
        const requestedPage =
          pageNumber != null && pageNumber >= 1
            ? Math.min(pageNumber, pdf.numPages)
            : preservedPageRef.current ?? 1
        preservedPageRef.current = null
        setCurrentPage(requestedPage)
        const zoom = isInitialDocumentLoadRef.current
          ? ZOOM_LEVELS[DEFAULT_ZOOM_INDEX] as number
          : ZOOM_LEVELS[zoomIndexRef.current] as number
        isInitialDocumentLoadRef.current = false
        await renderPage(requestedPage, zoom)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        console.error('Failed to load PDF:', err)
        setError('Failed to load PDF')
        setLoading(false)
      }
    }

    loadPdf()

    return () => {
      cancelled = true
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel()
      }
      if (textLayerInstanceRef.current) {
        textLayerInstanceRef.current.cancel()
      }
      if (loadingTaskRef.current) {
        try {
          loadingTaskRef.current.destroy()
        } catch {
          // Ignore errors from destroying pending or completed loads.
        }
        loadingTaskRef.current = null
      }
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy()
        } catch {
          // Ignore errors from destroying the document proxy.
        }
        pdfDocRef.current = null
      }
    }
  }, [documentId, pdfCacheKey, pageNumber, renderPage])

  // Sync to the row's page when the document or target page changes — not when the user
  // navigates with the toolbar (that would snap back to pageNumber on every click).
  useEffect(() => {
    if (
      pdfDocRef.current &&
      !loading &&
      pageNumber != null &&
      pageNumber >= 1 &&
      pageNumber <= totalPages
    ) {
      setCurrentPage(pageNumber)
    }
  }, [documentId, pageNumber, totalPages, loading])

  useEffect(() => {
    if (pdfDocRef.current && !loading) {
      renderPage(currentPage, ZOOM_LEVELS[zoomIndex] as number)
    }
  }, [currentPage, zoomIndex, renderPage, loading])

  useEffect(() => {
    updateHighlights(highlightData)
  }, [highlightData, updateHighlights])

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const zoomIn = () => {
    if (zoomIndex < ZOOM_LEVELS.length - 1) setZoomIndex(zoomIndex + 1)
  }

  const zoomOut = () => {
    if (zoomIndex > 0) setZoomIndex(zoomIndex - 1)
  }

  const handleDownload = async () => {
    if (downloading) return

    setDownloading(true)
    try {
      const response = await fetch(`/api/documents/${documentId}/pdf-content?_=${pdfCacheKey}`)
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = downloadFilename || `document_${documentId}.pdf`
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Failed to download attachment:', err)
    } finally {
      setDownloading(false)
    }
  }

  const handleRotate = async (degrees: 90 | -90) => {
    if (rotating || loading) return

    setRotating(true)
    setRotateError(null)
    try {
      await rotateDocumentPdfPage(documentId, currentPage, degrees)
      preservedPageRef.current = currentPage
      setPdfCacheKey(Date.now())
    } catch (err) {
      console.error('Failed to rotate PDF page:', err)
      setRotateError('Failed to rotate page')
    } finally {
      setRotating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading PDF...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500 text-sm">
        {error}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-gray-100">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-2 py-1.5 max-md:min-h-[48px] sm:px-3 sm:py-2">
        <div className="flex items-center gap-0.5 max-md:gap-1">
          <button
            onClick={zoomOut}
            disabled={zoomIndex <= 0}
            className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
            title="Zoom out"
            type="button"
          >
            <MagnifyingGlassMinusIcon className="h-5 w-5" />
          </button>
          <span className="w-12 shrink-0 text-center text-sm text-gray-600 tabular-nums">
            {Math.round((ZOOM_LEVELS[zoomIndex] ?? 1) * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={zoomIndex >= ZOOM_LEVELS.length - 1}
            className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
            title="Zoom in"
            type="button"
          >
            <MagnifyingGlassPlusIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 max-md:gap-1">
          <button
            onClick={() => void handleRotate(-90)}
            disabled={rotating}
            className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
            title="Rotate page counter-clockwise"
            type="button"
            aria-label="Rotate page counter-clockwise"
          >
            <ArrowPathIcon className="h-5 w-5 scale-x-[-1]" />
          </button>
          <button
            onClick={() => void handleRotate(90)}
            disabled={rotating}
            className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
            title="Rotate page clockwise"
            type="button"
            aria-label="Rotate page clockwise"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
          {rotating && (
            <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-500" aria-hidden />
          )}
        </div>

        <div className="ml-auto flex items-center gap-0.5 max-md:gap-1">
          {totalPages > 1 && (
            <>
              <button
                onClick={goToPrevPage}
                disabled={currentPage <= 1}
                className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
                type="button"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="shrink-0 px-1 text-sm text-gray-600 tabular-nums">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
                type="button"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </>
          )}
          {toolbarBeforeDownload}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center justify-center rounded-md p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 max-md:min-h-[44px] max-md:min-w-[44px]"
            title="Download attachment"
            type="button"
            aria-label="Download attachment"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      {rotateError && (
        <div className="border-b border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {rotateError}
        </div>
      )}
      <div className="flex-1 overflow-auto p-4">
        <div
          ref={pageWrapRef}
          className="relative mx-auto bookkeeper-pdf-page"
          style={{ width: 'fit-content' }}
        >
          <canvas ref={canvasRef} className="block rounded border border-gray-300 bg-white" />
          <div ref={textLayerRef} className="textLayer" />
        </div>
      </div>
    </div>
  )
}

export default memo(PdfViewer)
